import { PureComponent, Suspense } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import Actions from '../../../../../actions'
import Operations from '../../../../../operations'
import { find, isEqual, replace } from 'lodash'
import dayjs from 'dayjs'
import { EMAR_REASON_CODES } from '../../../../../common/constants'
import { colors } from '@lmc/common/constants'
import { showToast } from '../../../../../common/utils'
import { Spinner } from '@lmc/ui-kit'
import Form from '../../../../../components/forms'
import Field from '../../../../../components/fields'
import { Button, HeaderSection, InputSelect } from '@lmc/ui-kit'
import { objectDifference } from '../../../../../common/utils'
import style from './LogEditForm.module.less'
import TrashcanIcon from '@lmc/ui-kit/src/general/Icon/customShapes/Trashcan'
import { FlagTransparentIcon } from '@lmc/ui-kit/src/general/Icon/customShapes/Flag'
import { history } from '../../../../../store'
import { isSeizureItemLog } from '../utils'
import Selectors from 'selectors'
import { Alert, Icon } from '@lmc/ui-kit'
import { subject } from '@casl/ability'

const LogDetails = ({ timeLogged, testId }) => (
  <div>
    <div
      className="lmc-form-field-label"
      style={{ fontWeight: 'bold', paddingBottom: 6 }}
    >
      Time Logged
    </div>
    <div className={style.loggedByContainer}>
      <Suspense fallback={<div>Loading...</div>}>
        <Field
          formId={FORM_ID}
          keys="timeLogged"
          layout="column"
          type="datetime"
          values={{ timeLogged }}
          data-testid={
            testId ? `${testId}-time-logged-input` : 'time-logged-input'
          }
        />
      </Suspense>
    </div>
  </div>
)

export class LogEditForm extends PureComponent {
  constructor(props) {
    super(props)
    this.state = {}
  }

  componentDidMount() {
    const { addForm, log } = this.props

    this.setState({ ...log })

    addForm({
      initial: log,
      formId: FORM_ID
    })
  }

  getIcon = () => {
    const { category, item } = this.props
    const chosenItem = item && item.icon ? item : category
    return chosenItem && chosenItem.icon ? chosenItem.icon : ''
  }

  getIconColor = () => {
    const { category, item } = this.props
    const chosenItem = item && item.color ? item : category
    return chosenItem && chosenItem.color ? chosenItem.color : colors.darkblue
  }

  isHandoverDisabled = () => {
    if (this.props.deleted) {
      return true
    }
    const cutoff = dayjs().subtract(48, 'hours')
    return dayjs(this.props.log.timeLogged).isBefore(cutoff)
  }

  handleSubmit = () => {
    const { log, loadLog, updateItem, onEditComplete, fields } = this.props
    updateItem({
      keepFormOpen: true,
      url: `list/logs/${log.id}`,
      json: objectDifference(fields, log),
      onComplete: () => {
        // Re-fetch the log to get its revisions
        loadLog(log.id)
        showToast({ title: 'Updated a Log' })
        onEditComplete()
      }
    })
  }

  handleDelete = () => {
    const { log, loadLog, updateItem, onEditComplete } = this.props
    updateItem({
      url: `list/logs/${log.id}`,
      keepFormOpen: true,
      json: {
        deleted: true
      },
      onComplete: () => {
        loadLog(log.id)
        showToast({ type: 'info', title: 'Log Removed' })
        onEditComplete()
      }
    })
  }

  handleDeleteUndo = () => {
    const { log, loadLog, updateItem, onEditComplete } = this.props
    updateItem({
      url: `list/logs/${log.id}`,
      keepFormOpen: true,
      json: {
        deleted: false
      },
      onComplete: () => {
        loadLog(log.id)
        showToast({ title: 'Log Restored' })
        onEditComplete()
      }
    })
  }

  renderLogDetails = (deleted) => (
    <div
      style={styles.inputContainer}
      data-testid={
        this.props.testId
          ? `${this.props.testId}-log-details`
          : 'log-edit-form-log-details'
      }
    >
      {!deleted ? (
        <Suspense fallback={<div>Loading...</div>}>
          <Field
            formId={FORM_ID}
            keys="title"
            label="Title"
            layout="column"
            required
            type="text"
            values={this.props.fields}
            className={style.inputTitle}
            data-testid={
              this.props.testId
                ? `${this.props.testId}-title-input`
                : 'log-title-input'
            }
          />
        </Suspense>
      ) : (
        <div style={styles.logTitleContainer}>
          <div className="lmc-form-field-label" style={styles.logTitle}>
            {'Title '}
            <span style={{ color: colors.pink }}>*</span>
          </div>
          <div style={styles.logTitleText}>Log Removed</div>
        </div>
      )}
      {!deleted ? (
        <Suspense fallback={<div>Loading...</div>}>
          <Field
            formId={FORM_ID}
            keys="description"
            label="Description"
            layout="column"
            type="textarea"
            values={this.props.fields}
            style={{ height: 'auto' }}
            className={style.inputDescription}
            data-testid={
              this.props.testId
                ? `${this.props.testId}-description-input`
                : 'log-description-input'
            }
          />
        </Suspense>
      ) : (
        <div
          className="lmc-form-field-label"
          style={styles.logDescriptionEmpty}
        >
          Description
        </div>
      )}
    </div>
  )

  renderFooter = () => {
    const { fields, features } = this.props

    const isHandoverDisabled = this.isHandoverDisabled()
    return (
      <div
        className={style.footerContainer}
        data-testid={
          this.props.testId
            ? `${this.props.testId}-footer`
            : 'log-edit-form-footer'
        }
      >
        <div>
          {features?.features?.handovers?.enabled && (
            <div
              className={style.checkboxContainer}
              data-testid={
                this.props.testId
                  ? `${this.props.testId}-checkbox-container`
                  : 'log-edit-form-checkbox-container'
              }
            >
              {FlagTransparentIcon}
              <Suspense fallback={<div>Loading...</div>}>
                <Field
                  disabled={isHandoverDisabled}
                  formId={FORM_ID}
                  isNestedField
                  keys="isNextHandover"
                  label="Flag for handover?"
                  type="singleCheckbox"
                  values={fields}
                  data-testid={
                    this.props.testId
                      ? `${this.props.testId}-flag-handover-checkbox`
                      : 'flag-handover-checkbox'
                  }
                />
              </Suspense>
            </div>
          )}
          {features?.features?.handovers?.enabled && isHandoverDisabled && (
            <span className={style.handoverNote}>{HANDOVER_NOTE}</span>
          )}
        </div>
        <div className={style.loadingSpinner}>
          {this.props.isSubmitting ? <Spinner /> : null}
        </div>
      </div>
    )
  }

  renderMeasurement = (amountKey, key) => {
    const { fields } = this.props
    if (!amountKey) return null
    return (
      <Suspense fallback={<div>Loading...</div>}>
        <Field
          formId={FORM_ID}
          keys={key}
          amountKey={amountKey}
          // this is just a fallback label for validation errors
          label={replace(amountKey, '_', ' ')}
          layout="column"
          type="measurement"
          values={fields}
          onChange={(value) => this.setState({ [key]: value })}
          style={styles.measurementsContainer}
          data-testid={
            this.props.testId
              ? `${this.props.testId}-measurement-${key}-input`
              : `measurement-${key}-input`
          }
        />
      </Suspense>
    )
  }

  renderMeasurements = () => {
    const { item } = this.props
    if (isSeizureItemLog(item?.id)) {
      return null
    }

    if (item && (item.amountOneKey || item.amountTwoKey)) {
      return (
        <div>
          {item.amountOneKey &&
            this.renderMeasurement(item.amountOneKey, 'amount1')}
          {item.amountTwoKey &&
            this.renderMeasurement(item.amountTwoKey, 'amount2')}
        </div>
      )
    }

    return null
  }

  renderGoals = () => {
    const { logGoals, log, ...rest } = this.props

    return (
      <div
        style={styles.goalsContainer}
        data-testid={
          this.props.testId
            ? `${this.props.testId}-goals-container`
            : 'log-edit-form-goals-container'
        }
      >
        <InputSelect
          mode="multiple"
          layout="column"
          label="Linked Goals"
          placeholder="Add goals"
          onChange={(value) =>
            rest.setFormField({
              key: 'goals',
              value
            })
          }
          defaultValue={log.goals}
          options={
            logGoals?.map((goal) => ({
              label: goal?.title || 'N/A',
              value: goal?.id
            })) || []
          }
          data-testid={
            this.props.testId
              ? `${this.props.testId}-linked-goals-select`
              : 'linked-goals-select'
          }
        />
      </div>
    )
  }

  render() {
    const {
      loggedBy,
      deleted,
      fields,
      features,
      log,
      item,
      category,
      initial,
      isSubmitting,
      onEditComplete,
      hasGoals,
      hasAccess
    } = this.props
    const isSaveDisabled = isEqual(initial, fields)

    const hasRolePermission = hasAccess(
      ['carehome-admin'],
      [
        {
          i: 'delete',
          this: subject('Log', {})
        }
      ]
    )
    return (
      <div
        data-testid={
          this.props.testId ? `${this.props.testId}-edit-form` : 'log-edit-form'
        }
      >
        <HeaderSection
          backLink
          title={log.title}
          onClick={history.back}
          iconColor={category?.color}
          iconContent={
            <img src={item?.icon} alt="icon" style={{ height: 20 }} />
          }
          testId={this.props.testId}
          rightChildren={
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              <Button
                id="log-remove"
                onClick={this.handleDelete}
                variant="danger"
                style={{ height: 24 }}
                icon={TrashcanIcon}
                data-testid={
                  this.props.testId
                    ? `${this.props.testId}-remove-log`
                    : 'remove-log'
                }
                loading={isSubmitting}
                disabledByPermission={!hasRolePermission}
              />
              <Button
                onClick={() => {
                  if (onEditComplete && this.props.onCancel) {
                    this.props.onCancel(onEditComplete)
                  } else {
                    // eslint-disable-next-line no-console
                    console.error(
                      '[ERROR] onEditComplete or onCancel is not defined [LogEditForm]'
                    )
                  }
                }}
                style={styles.saveButton}
                variant="default"
                data-testid={
                  this.props.testId
                    ? `${this.props.testId}-cancel-log`
                    : 'cancel-log'
                }
              >
                Cancel
              </Button>
              <Button
                disabled={isSaveDisabled}
                onClick={this.handleSubmit}
                style={styles.saveButton}
                variant="primary"
                icon={<Icon name="CheckOutlined" />}
                data-testid={
                  this.props.testId
                    ? `${this.props.testId}-save-log`
                    : 'save-log'
                }
                loading={isSubmitting}
              >
                Save
              </Button>
            </div>
          }
        />
        {item?.itemId === 'painchek' && (
          <Alert
            message="Edits don’t automatically sync between Log my Care and PainChek. You’ll need to make the same edits in both systems."
            type="warning"
          />
        )}
        {this.renderLogDetails(deleted)}
        {this.renderFooter()}
        <div
          className={style.optionalFields}
          data-testid={
            this.props.testId
              ? `${this.props.testId}-optional-fields`
              : 'log-edit-form-optional-fields'
          }
        >
          {!deleted && this.renderMeasurements()}
          {features.eMAR && fields?.reasonCode && (
            <div
              style={styles.reasonCodeContainer}
              data-testid={
                this.props.testId
                  ? `${this.props.testId}-reason-code-container`
                  : 'log-edit-form-reason-code-container'
              }
            >
              <Suspense fallback={<div>Loading...</div>}>
                <Field
                  formId={FORM_ID}
                  required
                  keys="reasonCode"
                  label="Outcome"
                  options={[
                    {
                      value: 'AD',
                      label: 'Administered',
                      icon: 'check',
                      iconColor: colors.green
                    },
                    ...EMAR_REASON_CODES.map((code) => ({
                      ...code,
                      iconLabel: code.value,
                      iconColor: colors.red
                    }))
                  ]}
                  onChange={(e) => this.setState({ reasonCode: e })}
                  type="select"
                  values={fields}
                  data-testid={
                    this.props.testId
                      ? `${this.props.testId}-outcome-select`
                      : 'outcome-select'
                  }
                />
              </Suspense>
            </div>
          )}
        </div>
        <LogDetails
          timeLogged={log.timeLogged}
          loggedBy={loggedBy}
          deleted={deleted}
          testId={this.props.testId}
        />
        {hasGoals && this.renderGoals()}
      </div>
    )
  }
}

const FORM_ID = 'editLog'
const HANDOVER_NOTE = 'After 48 hours, logs cannot be flagged for handover.'

const styles = {
  inputContainer: {
    paddingTop: 24
  },
  reasonCodeContainer: {
    maxWidth: 300
  },
  dateTimeLogged: {
    marginTop: 5,
    display: 'flex',
    marginBottom: 19
  },
  fieldContainer: {
    paddingBottom: 20
  },
  headerContainer: {
    display: 'flex',
    paddingTop: 30
  },
  icon: {
    width: 20
  },
  iconContainer: {
    alignItems: 'center',
    borderRadius: 48,
    display: 'flex',
    flexShrink: 0,
    justifyContent: 'center',
    height: 48,
    width: 48
  },
  logDescriptionEmpty: {
    fontWeight: 600,
    paddingBottom: 155
  },
  logTitle: {
    marginRight: 5,
    fontWeight: 600
  },
  logTitleContainer: {
    paddingBottom: 34
  },
  logTitleText: {
    paddingTop: 10
  },
  logRemovedBanner: {
    textAlign: 'center',
    marginTop: 20,
    marginBottom: '-1em'
  },
  logRemovedDateTime: {
    marginTop: 8,
    marginRight: 10
  },
  pictureContainer: {
    flexShrink: 0,
    marginRight: 15
  },
  primaryContainer: {
    display: 'flex',
    paddingTop: 30
  },
  removeButton: {
    color: 'white'
  },
  saveButton: {
    marginLeft: 9
  },
  savedText: {
    alignSelf: 'center',
    color: colors.darkgrey
  },
  secondaryContainer: {
    flex: 1,
    paddingTop: 13
  },
  dotText: {
    marginTop: 8,
    marginRight: 10
  },
  timeLoggedText: {
    marginTop: 8
  },
  undoLink: {
    textDecoration: 'none',
    color: colors.pink
  },
  measurementsContainer: {
    paddingBottom: 13
  },
  goalsContainer: {
    marginBottom: 10
  }
}

LogEditForm.propTypes = {
  addForm: PropTypes.func.isRequired,
  category: PropTypes.object,
  item: PropTypes.object,
  loadLog: PropTypes.func.isRequired,
  updateItem: PropTypes.func.isRequired,
  onEditComplete: PropTypes.func.isRequired,
  log: PropTypes.object,
  loggedBy: PropTypes.object,
  resident: PropTypes.object,
  deleted: PropTypes.bool,
  logGoals: PropTypes.array,
  fields: PropTypes.object,
  hasGoals: PropTypes.bool,
  preferredPeopleNominative: PropTypes.string,
  testId: PropTypes.string
}

const mapStateToProps = (state, { log }) => ({
  category: find(state.data['log-categories'], { id: log.category }),
  features: state.global.home,
  item: find(state.data['log-category-items'], { id: log.item }),
  loggedBy: find(state.data.carers, { id: log?.loggedBy }),
  resident: find(state.data.residents, { id: log.resident }),
  deleted: log.deleted,
  hasGoals: state.global.home.goals,
  preferredPeopleNominative: state.global.home.preferredPeopleNominative,
  hasAccess: (roles, abilities) =>
    // @ts-ignore
    Selectors.checkRoleAccess(state, { roles, abilities })
})

const mapDispatchToProps = (dispatch) => ({
  addForm: (data) => dispatch(Actions.addForm(data)),
  loadLog: (id) =>
    dispatch(Operations.loadListItem('logs', id, { withRevisions: true }))
})

const wrappedForm = connect(mapStateToProps, mapDispatchToProps)(LogEditForm)

export default Form(wrappedForm, FORM_ID)
